import { LoadingOutlined } from "@ant-design/icons";
import { useAuthFetch } from "@p0security/frontend-core";
import {
  SubmitRequestRequestBody,
  SubmitRequestResponseBody,
  WebAction,
  WebModalState,
} from "@p0security/shared/integrations/notifiers/web-requests/types";
import { assertNever } from "@p0security/shared/types/index";
import {
  Alert,
  Button,
  Flex,
  List,
  Modal,
  Spacing,
  Typography,
} from "@p0security/ui";
import { LinkWithEnvironment } from "components/Assessment/components/LinkWithEnvironment";
import { useCallback, useEffect, useState } from "react";
import { FormProvider } from "react-hook-form";

import { BlocksRenderer } from "./BlockRenderers";
import { useBlockForm } from "./useBlockForm";

export const RequestAccessModal: React.FC<{
  open: boolean;
  onCancel: () => void;
  /** If present, the modal is prefilled from this previously created request */
  requestId?: string;
}> = ({ open, onCancel, requestId }) => {
  const [modal, contextHolder] = Modal.useModal();
  const [initializing, setInitializing] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [urls, setUrls] = useState<string[] | undefined>(undefined);

  const {
    blocks,
    actions,
    form,
    watching,
    error,
    clearError,
    setError,
    handleFetchOptions,
    dispatch,
    resetState,
  } = useBlockForm({
    dispatchEndpoint: "integrations/web-requests/request-modal",
    suggestionsEndpoint: "integrations/web-requests/suggestions",
    enabled: !initializing,
  });

  const submitAuthFetch = useAuthFetch(setError);
  const { isDirty } = form.formState;

  useEffect(() => {
    if (open) {
      setInitializing(true);
      dispatch({}, requestId).finally(() => setInitializing(false));
    }
  }, [open, dispatch, requestId]);

  const handleSubmit = useCallback(
    async (data: WebModalState) => {
      setSubmitting(true);
      try {
        const json: SubmitRequestRequestBody = {
          values: data,
        };

        const response = await submitAuthFetch(
          "integrations/web-requests/submit",
          {
            method: "POST",
            json,
          }
        );
        if (!response?.ok) {
          return;
        }
        const result: SubmitRequestResponseBody = await response.json();
        if (result.ok) {
          setUrls(result.urls);
          form.reset();
        } else {
          setError(result.error);
        }
      } catch (e) {
        console.error(e);
        setError(
          "Error submitting request, check console for more information."
        );
      } finally {
        setSubmitting(false);
      }
    },
    [form, submitAuthFetch, setError]
  );

  const handleCloseModal = useCallback(() => {
    if (isDirty) {
      modal.confirm({
        title: "Discard changes?",
        content: "You have unsaved changes that will be lost.",
        okText: "Yes, discard changes",
        okType: "danger",
        maskClosable: true,
        onOk() {
          onCancel();
          resetState();
          setInitializing(true);
          setSubmitting(false);
          setUrls(undefined);
        },
      });
    } else {
      onCancel();
      resetState();
      setInitializing(true);
      setSubmitting(false);
      setUrls(undefined);
    }
  }, [isDirty, modal, onCancel, resetState]);

  const renderSubmittedRequests = useCallback((item: string) => {
    return (
      <div key={item}>
        Created a new access{" "}
        <LinkWithEnvironment to={item}>request</LinkWithEnvironment>
      </div>
    );
  }, []);

  return (
    <>
      <Modal
        title={
          <Flex>
            <Spacing asChild m={0}>
              <Typography.Title level={2} className="p-0">
                Request Access
              </Typography.Title>
            </Spacing>
          </Flex>
        }
        open={open}
        footer={null}
        onCancel={handleCloseModal}
        loading={initializing}
        maskClosable={false}
      >
        {submitting ? (
          <LoadingOutlined />
        ) : urls ? (
          <List dataSource={urls} renderItem={renderSubmittedRequests} />
        ) : (
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <Flex vertical gap="middle">
                {error && (
                  <Alert
                    message="Error"
                    description={error}
                    type="error"
                    showIcon
                    closable
                    onClose={clearError}
                  />
                )}

                <BlocksRenderer
                  blocks={blocks}
                  onFetchOptions={handleFetchOptions}
                  watching={watching}
                />

                <ActionsRenderer
                  actions={actions}
                  onCancel={handleCloseModal}
                />
              </Flex>
            </form>
          </FormProvider>
        )}
      </Modal>
      {contextHolder}
    </>
  );
};

const ActionsRenderer: React.FC<{
  actions: WebAction[];
  onCancel: () => void;
}> = ({ actions, onCancel }) => {
  return (
    <Flex justify="flex-end" gap="small">
      {actions.map((action) => {
        return (
          <ActionRenderer key={action.id} action={action} onCancel={onCancel} />
        );
      })}
    </Flex>
  );
};

const ActionRenderer: React.FC<{
  action: WebAction;
  onCancel: () => void;
}> = ({ action, onCancel }) => {
  switch (action.type) {
    case "cancel":
      return (
        <Button onClick={onCancel} type="default" htmlType="button">
          {action.label}
        </Button>
      );
    case "submit":
      return (
        <Button type="primary" htmlType="submit">
          {action.label}
        </Button>
      );
    default:
      assertNever(action.type);
      return <>Unsupported action type</>;
  }
};
